import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useAuth,
} from "../../auth/AuthContext";

import type {
  EmployeeProfile,
  UserRole,
} from "../../auth/types";

import {
  alterarStatusFuncionario,
  criarFuncionario,
  editarFuncionario,
  loadFuncionarios,
  redefinirSenhaFuncionario,
} from "./funcionarios.service";

import "./Funcionarios.css";


const ROLES: {
  value: UserRole;
  label: string;
}[] = [
  {
    value:
      "administrador",
    label:
      "Administrador",
  },
  {
    value:
      "gerente",
    label:
      "Gerente",
  },
  {
    value:
      "professor",
    label:
      "Professor",
  },
  {
    value:
      "caixa",
    label:
      "Caixa",
  },
  {
    value:
      "funcionario",
    label:
      "Funcionário",
  },
];


function roleLabel(
  role: UserRole,
) {
  return (
    ROLES.find(
      (item) =>
        item.value ===
        role,
    )?.label ??
    role
  );
}


function getErrorMessage(
  error: unknown,
  fallback: string,
) {
  if (
    typeof error ===
      "object" &&
    error !== null &&
    "message" in error
  ) {
    const message =
      String(
        (
          error as {
            message?: string;
          }
        ).message ??
          "",
      ).trim();

    if (message) {
      return message;
    }
  }

  return fallback;
}


export default function Funcionarios() {
  const {
    can,
    profile,
  } =
    useAuth();


  const [
    funcionarios,
    setFuncionarios,
  ] =
    useState<
      EmployeeProfile[]
    >([]);


  const [
    loading,
    setLoading,
  ] =
    useState(
      true,
    );


  const [
    saving,
    setSaving,
  ] =
    useState(
      false,
    );


  const [
    search,
    setSearch,
  ] =
    useState(
      "",
    );


  const [
    error,
    setError,
  ] =
    useState(
      "",
    );


  /*
   * =====================================================
   * CRIAÇÃO
   * =====================================================
   */

  const [
    showCreate,
    setShowCreate,
  ] =
    useState(
      false,
    );


  const [
    nome,
    setNome,
  ] =
    useState(
      "",
    );


  const [
    loginId,
    setLoginId,
  ] =
    useState(
      "",
    );


  const [
    senha,
    setSenha,
  ] =
    useState(
      "",
    );


  const [
    confirmarSenha,
    setConfirmarSenha,
  ] =
    useState(
      "",
    );


  const [
    cargo,
    setCargo,
  ] =
    useState<UserRole>(
      "funcionario",
    );


  /*
   * =====================================================
   * EDIÇÃO
   * =====================================================
   */

  const [
    editing,
    setEditing,
  ] =
    useState<
      EmployeeProfile | null
    >(
      null,
    );


  const [
    editNome,
    setEditNome,
  ] =
    useState(
      "",
    );


  const [
    editLoginId,
    setEditLoginId,
  ] =
    useState(
      "",
    );


  const [
    editCargo,
    setEditCargo,
  ] =
    useState<UserRole>(
      "funcionario",
    );


  /*
   * =====================================================
   * REDEFINIÇÃO DE SENHA
   * =====================================================
   */

  const [
    passwordTarget,
    setPasswordTarget,
  ] =
    useState<
      EmployeeProfile | null
    >(
      null,
    );


  const [
    novaSenha,
    setNovaSenha,
  ] =
    useState(
      "",
    );


  const [
    confirmarNovaSenha,
    setConfirmarNovaSenha,
  ] =
    useState(
      "",
    );


  async function carregar() {
    try {
      setLoading(
        true,
      );

      setError(
        "",
      );


      const result =
        await loadFuncionarios();


      setFuncionarios(
        result,
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        "Não foi possível carregar os funcionários.",
      );
    } finally {
      setLoading(
        false,
      );
    }
  }


  useEffect(
    () => {
      carregar();
    },
    [],
  );


  const filtered =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();


        if (!term) {
          return funcionarios;
        }


        return funcionarios.filter(
          (item) =>
            item.nome
              .toLowerCase()
              .includes(
                term,
              ) ||
            (
              item.login_id ??
              ""
            )
              .toLowerCase()
              .includes(
                term,
              ) ||
            roleLabel(
              item.cargo,
            )
              .toLowerCase()
              .includes(
                term,
              ),
        );
      },
      [
        funcionarios,
        search,
      ],
    );


  function replaceFuncionario(
    updated:
      EmployeeProfile,
  ) {
    setFuncionarios(
      (current) =>
        current
          .map(
            (
              item,
            ) =>
              item.id ===
              updated.id
                ? updated
                : item,
          )
          .sort(
            (
              a,
              b,
            ) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
    );
  }


  /*
   * =====================================================
   * CRIAR
   * =====================================================
   */

  function closeCreate() {
    if (saving) {
      return;
    }


    setShowCreate(
      false,
    );

    setNome(
      "",
    );

    setLoginId(
      "",
    );

    setSenha(
      "",
    );

    setConfirmarSenha(
      "",
    );

    setCargo(
      "funcionario",
    );

    setError(
      "",
    );
  }


  async function handleCreate() {
    const cleanNome =
      nome.trim();

    const cleanLogin =
      loginId
        .trim()
        .toLowerCase();


    if (!cleanNome) {
      setError(
        "Informe o nome do funcionário.",
      );

      return;
    }


    if (!cleanLogin) {
      setError(
        "Informe o ID de acesso.",
      );

      return;
    }


    if (
      senha.length < 6
    ) {
      setError(
        "A senha deve possuir pelo menos 6 caracteres.",
      );

      return;
    }


    if (
      senha !==
      confirmarSenha
    ) {
      setError(
        "As senhas não conferem.",
      );

      return;
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      const created =
        await criarFuncionario({
          nome:
            cleanNome,

          loginId:
            cleanLogin,

          senha,

          cargo,
        });


      setFuncionarios(
        (current) =>
          [
            ...current,
            created,
          ].sort(
            (
              a,
              b,
            ) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
      );


      setShowCreate(
        false,
      );

      setNome(
        "",
      );

      setLoginId(
        "",
      );

      setSenha(
        "",
      );

      setConfirmarSenha(
        "",
      );

      setCargo(
        "funcionario",
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        getErrorMessage(
          err,
          "Não foi possível criar o funcionário.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  /*
   * =====================================================
   * EDITAR
   * =====================================================
   */

  function openEdit(
    funcionario:
      EmployeeProfile,
  ) {
    setError(
      "",
    );

    setEditing(
      funcionario,
    );

    setEditNome(
      funcionario.nome,
    );

    setEditLoginId(
      funcionario.login_id ??
        "",
    );

    setEditCargo(
      funcionario.cargo,
    );
  }


  function closeEdit() {
    if (saving) {
      return;
    }


    setEditing(
      null,
    );

    setError(
      "",
    );
  }


  async function handleEdit() {
    if (!editing) {
      return;
    }


    const cleanNome =
      editNome.trim();

    const cleanLogin =
      editLoginId
        .trim()
        .toLowerCase();


    if (!cleanNome) {
      setError(
        "Informe o nome do funcionário.",
      );

      return;
    }


    if (!cleanLogin) {
      setError(
        "Informe o ID de acesso.",
      );

      return;
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      const updated =
        await editarFuncionario({
          id:
            editing.id,

          nome:
            cleanNome,

          loginId:
            cleanLogin,

          cargo:
            editCargo,
        });


      replaceFuncionario(
        updated,
      );


      setEditing(
        null,
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        getErrorMessage(
          err,
          "Não foi possível editar o funcionário.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  /*
   * =====================================================
   * ATIVAR / DESATIVAR
   * =====================================================
   */

  async function handleToggleStatus(
    funcionario:
      EmployeeProfile,
  ) {
    const novoStatus =
      !funcionario.ativo;


    if (
      !novoStatus
    ) {
      const confirmed =
        window.confirm(
          `Desativar o acesso de ${funcionario.nome}?`,
        );


      if (!confirmed) {
        return;
      }
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      const updated =
        await alterarStatusFuncionario(
          funcionario.id,
          novoStatus,
        );


      replaceFuncionario(
        updated,
      );


      if (
        editing?.id ===
        updated.id
      ) {
        setEditing(
          updated,
        );
      }
    } catch (err) {
      console.error(
        err,
      );

      setError(
        getErrorMessage(
          err,
          novoStatus
            ? "Não foi possível ativar o funcionário."
            : "Não foi possível desativar o funcionário.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  /*
   * =====================================================
   * SENHA
   * =====================================================
   */

  function openPassword(
    funcionario:
      EmployeeProfile,
  ) {
    setError(
      "",
    );

    setPasswordTarget(
      funcionario,
    );

    setNovaSenha(
      "",
    );

    setConfirmarNovaSenha(
      "",
    );
  }


  function closePassword() {
    if (saving) {
      return;
    }


    setPasswordTarget(
      null,
    );

    setNovaSenha(
      "",
    );

    setConfirmarNovaSenha(
      "",
    );

    setError(
      "",
    );
  }


  async function handlePassword() {
    if (
      !passwordTarget
    ) {
      return;
    }


    if (
      novaSenha.length < 6
    ) {
      setError(
        "A nova senha deve possuir pelo menos 6 caracteres.",
      );

      return;
    }


    if (
      novaSenha !==
      confirmarNovaSenha
    ) {
      setError(
        "As senhas não conferem.",
      );

      return;
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      await redefinirSenhaFuncionario(
        passwordTarget.id,
        novaSenha,
      );


      setPasswordTarget(
        null,
      );

      setNovaSenha(
        "",
      );

      setConfirmarNovaSenha(
        "",
      );

      window.alert(
        "Senha redefinida com sucesso.",
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        getErrorMessage(
          err,
          "Não foi possível redefinir a senha.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  if (
    !can(
      "funcionarios.view",
    )
  ) {
    return (
      <section className="funcionarios-page">
        <div className="funcionarios-empty">
          Você não possui acesso aos funcionários.
        </div>
      </section>
    );
  }


  return (
    <section className="funcionarios-page">
      <div className="funcionarios-header">
        <div>
          <span className="funcionarios-eyebrow">
            EQUIPE
          </span>

          <h2>
            Funcionários
          </h2>

          <p>
            Gerencie acessos, cargos e status da equipe.
          </p>
        </div>


        {can(
          "funcionarios.create",
        ) && (
          <button
            type="button"
            className="funcionarios-primary"
            onClick={() => {
              setError(
                "",
              );

              setShowCreate(
                true,
              );
            }}
          >
            + Novo funcionário
          </button>
        )}
      </div>


      <div className="funcionarios-toolbar">
        <input
          type="search"
          placeholder="Buscar por nome, login ou cargo..."
          value={
            search
          }
          onChange={(
            event,
          ) =>
            setSearch(
              event.target
                .value,
            )
          }
        />

        <span>
          {
            filtered.length
          }{" "}
          {
            filtered.length ===
            1
              ? "funcionário"
              : "funcionários"
          }
        </span>
      </div>


      {error &&
        !showCreate &&
        !editing &&
        !passwordTarget && (
          <div className="funcionarios-error">
            {error}
          </div>
        )}


      {loading ? (
        <div className="funcionarios-empty">
          Carregando funcionários...
        </div>
      ) : filtered.length ===
        0 ? (
        <div className="funcionarios-empty">
          Nenhum funcionário encontrado.
        </div>
      ) : (
        <div className="funcionarios-grid">
          {filtered.map(
            (
              funcionario,
            ) => (
              <article
                key={
                  funcionario.id
                }
                className="funcionario-card"
              >
                <div className="funcionario-main">
                  <div className="funcionario-avatar">
                    {funcionario.nome
                      .charAt(
                        0,
                      )
                      .toUpperCase()}
                  </div>


                  <div className="funcionario-info">
                    <strong>
                      {
                        funcionario.nome
                      }
                    </strong>

                    <span>
                      {funcionario.login_id
                        ? `ID: ${funcionario.login_id}`
                        : funcionario.email ||
                          "Sem login"}
                    </span>
                  </div>
                </div>


                <div className="funcionario-meta">
                  <span className="funcionario-role">
                    {roleLabel(
                      funcionario.cargo,
                    )}
                  </span>


                  <span
                    className={
                      funcionario.ativo
                        ? "funcionario-status ativo"
                        : "funcionario-status inativo"
                    }
                  >
                    {funcionario.ativo
                      ? "Ativo"
                      : "Inativo"}
                  </span>
                </div>


                {can(
                  "funcionarios.edit",
                ) && (
                  <div className="funcionario-actions">
                    <button
                      type="button"
                      onClick={() =>
                        openEdit(
                          funcionario,
                        )
                      }
                    >
                      Editar
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        openPassword(
                          funcionario,
                        )
                      }
                    >
                      Senha
                    </button>

                    <button
                      type="button"
                      className={
                        funcionario.ativo
                          ? "danger"
                          : "success"
                      }
                      onClick={() =>
                        handleToggleStatus(
                          funcionario,
                        )
                      }
                      disabled={
                        saving ||
                        (
                          funcionario.id ===
                            profile?.id &&
                          funcionario.ativo
                        )
                      }
                    >
                      {funcionario.ativo
                        ? "Desativar"
                        : "Ativar"}
                    </button>
                  </div>
                )}
              </article>
            ),
          )}
        </div>
      )}


      {showCreate && (
        <div
          className="funcionario-modal-backdrop"
          onClick={
            closeCreate
          }
        >
          <div
            className="funcionario-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="funcionario-modal-header">
              <div>
                <span className="funcionarios-eyebrow">
                  ACESSO
                </span>

                <h3>
                  Novo funcionário
                </h3>
              </div>

              <button
                type="button"
                onClick={
                  closeCreate
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <label>
              Nome

              <input
                autoFocus
                value={
                  nome
                }
                onChange={(
                  event,
                ) =>
                  setNome(
                    event.target
                      .value,
                  )
                }
              />
            </label>


            <label>
              ID de acesso

              <input
                value={
                  loginId
                }
                onChange={(
                  event,
                ) =>
                  setLoginId(
                    event.target
                      .value,
                  )
                }
              />
            </label>


            <label>
              Cargo

              <select
                value={
                  cargo
                }
                onChange={(
                  event,
                ) =>
                  setCargo(
                    event.target
                      .value as UserRole,
                  )
                }
              >
                {ROLES.map(
                  (
                    role,
                  ) => (
                    <option
                      key={
                        role.value
                      }
                      value={
                        role.value
                      }
                    >
                      {
                        role.label
                      }
                    </option>
                  ),
                )}
              </select>
            </label>


            <div className="funcionario-modal-grid">
              <label>
                Senha inicial

                <input
                  type="password"
                  value={
                    senha
                  }
                  onChange={(
                    event,
                  ) =>
                    setSenha(
                      event.target
                        .value,
                    )
                  }
                />
              </label>


              <label>
                Confirmar senha

                <input
                  type="password"
                  value={
                    confirmarSenha
                  }
                  onChange={(
                    event,
                  ) =>
                    setConfirmarSenha(
                      event.target
                        .value,
                    )
                  }
                />
              </label>
            </div>


            {error && (
              <div className="funcionarios-error">
                {error}
              </div>
            )}


            <div className="funcionario-modal-actions">
              <button
                type="button"
                className="funcionario-cancel"
                onClick={
                  closeCreate
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>

              <button
                type="button"
                className="funcionarios-primary"
                onClick={
                  handleCreate
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "Criando..."
                  : "Criar funcionário"}
              </button>
            </div>
          </div>
        </div>
      )}


      {editing && (
        <div
          className="funcionario-modal-backdrop"
          onClick={
            closeEdit
          }
        >
          <div
            className="funcionario-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="funcionario-modal-header">
              <div>
                <span className="funcionarios-eyebrow">
                  EDIÇÃO
                </span>

                <h3>
                  Editar funcionário
                </h3>
              </div>

              <button
                type="button"
                onClick={
                  closeEdit
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <label>
              Nome

              <input
                autoFocus
                value={
                  editNome
                }
                onChange={(
                  event,
                ) =>
                  setEditNome(
                    event.target
                      .value,
                  )
                }
              />
            </label>


            <label>
              ID de acesso

              <input
                value={
                  editLoginId
                }
                onChange={(
                  event,
                ) =>
                  setEditLoginId(
                    event.target
                      .value,
                  )
                }
              />
            </label>


            <label>
              Cargo

              <select
                value={
                  editCargo
                }
                onChange={(
                  event,
                ) =>
                  setEditCargo(
                    event.target
                      .value as UserRole,
                  )
                }
              >
                {ROLES.map(
                  (
                    role,
                  ) => (
                    <option
                      key={
                        role.value
                      }
                      value={
                        role.value
                      }
                    >
                      {
                        role.label
                      }
                    </option>
                  ),
                )}
              </select>
            </label>


            {error && (
              <div className="funcionarios-error">
                {error}
              </div>
            )}


            <div className="funcionario-modal-actions">
              <button
                type="button"
                className="funcionario-cancel"
                onClick={
                  closeEdit
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>

              <button
                type="button"
                className="funcionarios-primary"
                onClick={
                  handleEdit
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "Salvando..."
                  : "Salvar alterações"}
              </button>
            </div>
          </div>
        </div>
      )}


      {passwordTarget && (
        <div
          className="funcionario-modal-backdrop"
          onClick={
            closePassword
          }
        >
          <div
            className="funcionario-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="funcionario-modal-header">
              <div>
                <span className="funcionarios-eyebrow">
                  SEGURANÇA
                </span>

                <h3>
                  Redefinir senha
                </h3>

                <p>
                  {
                    passwordTarget.nome
                  }
                </p>
              </div>

              <button
                type="button"
                onClick={
                  closePassword
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <div className="funcionario-modal-grid">
              <label>
                Nova senha

                <input
                  autoFocus
                  type="password"
                  value={
                    novaSenha
                  }
                  onChange={(
                    event,
                  ) =>
                    setNovaSenha(
                      event.target
                        .value,
                    )
                  }
                />
              </label>


              <label>
                Confirmar senha

                <input
                  type="password"
                  value={
                    confirmarNovaSenha
                  }
                  onChange={(
                    event,
                  ) =>
                    setConfirmarNovaSenha(
                      event.target
                        .value,
                    )
                  }
                />
              </label>
            </div>


            {error && (
              <div className="funcionarios-error">
                {error}
              </div>
            )}


            <div className="funcionario-modal-actions">
              <button
                type="button"
                className="funcionario-cancel"
                onClick={
                  closePassword
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>

              <button
                type="button"
                className="funcionarios-primary"
                onClick={
                  handlePassword
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "Salvando..."
                  : "Redefinir senha"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}